import { Trash2, Loader2, AlertTriangle, X, RotateCw, ArrowDown } from "lucide-react";
import { useOfflineEpisode } from "@/hooks/useOfflineEpisode";
import { useOfflineStorageEstimate } from "@/hooks/useOfflineStorageEstimate";
import { QUOTA_WARNING_PERCENT, QUOTA_CRITICAL_PERCENT } from "@/lib/offlineEpisodes";
import { isPWA } from "@/lib/platform";
import { requestInstallPrompt } from "@/lib/installPromptBus";
import { toast } from "sonner";
import { useEffect } from "react";

interface OfflineEpisodeButtonProps {
  episodeId: string;
  audioUrl?: string | null;
  title: string;
  podcastName: string;
  artworkUrl?: string;
  durationSeconds?: number;
}

// Shared comic button skeleton that mirrors the Note button treatment in the
// library action strip: 2px border, hard 3px shadow, halftone overlay, and the
// same press-into-shadow micro-interaction.
const comicBase =
  "comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95";

const shadowState =
  "shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px]";

export default function OfflineEpisodeButton({
  episodeId,
  audioUrl,
  title,
  podcastName,
  artworkUrl,
  durationSeconds,
}: OfflineEpisodeButtonProps) {
  const { isOffline, isDownloading, progress, error, save, cancel, retry, remove } =
    useOfflineEpisode(episodeId, audioUrl);
  const { estimate } = useOfflineStorageEstimate();

  const nearQuota = !!estimate && estimate.percent >= QUOTA_WARNING_PERCENT;
  const criticalQuota = !!estimate && estimate.percent >= QUOTA_CRITICAL_PERCENT;

  useEffect(() => {
    if (criticalQuota && isOffline) {
      toast.warning("Your device storage is nearly full. Consider removing older offline episodes.");
    }
  }, [criticalQuota, isOffline]);

  if (!audioUrl) return null;

  const handleSave = async () => {
    // Offline download relies on the Cache API + service worker. In iOS
    // Safari (and any browser where we're not running as an installed
    // PWA on mobile), the download is unreliable and storage gets evicted
    // aggressively. Nudge the user to install the app instead.
    if (!isPWA()) {
      requestInstallPrompt("feature-blocked", { force: true });
      return;
    }
    try {
      if (nearQuota) {
        toast.warning(
          "Your device storage is running low. The download may fail if you run out of space."
        );
      }
      await save({
        episodeId,
        audioUrl,
        title,
        podcastName,
        artworkUrl,
        durationSeconds,
      });
      toast.success("Episode saved for offline listening");
    } catch (err) {
      console.error("Offline save failed:", err);
      toast.error(err instanceof Error ? err.message : "Could not save episode offline");
    }
  };

  const handleRemove = async () => {
    try {
      await remove();
      toast.success("Offline episode removed");
    } catch (err) {
      console.error("Offline remove failed:", err);
      toast.error("Could not remove offline episode");
    }
  };

  const handleCancel = () => {
    cancel();
    toast.message("Download canceled");
  };

  const handleRetry = async () => {
    try {
      await retry();
      toast.success("Episode saved for offline listening");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    }
  };

  // Downloading state: a comic progress button with a separate comic cancel button.
  if (isDownloading) {
    return (
      <div className="inline-flex items-center gap-1">
        <div
          className={`${comicBase} ${shadowState} bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)]`}
          aria-label={`Downloading ${progress}%`}
          title={`Downloading — ${progress}%. Click the X to cancel.`}
        >
          <Loader2 className="w-3 h-3 animate-spin relative z-10" strokeWidth={2.5} />
          <span className="relative z-10">{progress}%</span>
          <span
            className="absolute bottom-0 left-0 h-1 bg-[hsl(195,80%,28%)]/30 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <button
          type="button"
          onClick={handleCancel}
          title="Cancel this offline download."
          aria-label="Cancel download"
          className={`${comicBase} ${shadowState} bg-[hsl(var(--destructive)/10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]`}
        >
          <X className="w-3 h-3 relative z-10" strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  // Error state: retry comic button (only when running as installed PWA).
  if (error && isPWA()) {
    return (
      <button
        type="button"
        onClick={handleRetry}
        title={`Download failed: ${error}. Click to retry.`}
        aria-label="Retry offline download"
        className={`${comicBase} ${shadowState} bg-[hsl(var(--destructive))] text-[hsl(var(--primary-foreground))] border-[hsl(0,70%,40%)]`}
      >
        <RotateCw className="w-3 h-3 relative z-10" strokeWidth={2.5} />
        <span>Retry</span>
      </button>
    );
  }

  const tooltip = isOffline
    ? "This episode is available offline. Click to remove it from your device."
    : !isPWA()
    ? "Install AskThePods to download episodes for offline listening."
    : criticalQuota
    ? "Your device storage is nearly full. Free up space before downloading more episodes."
    : "Download this episode so you can listen without an internet connection.";

  const isDanger = criticalQuota && !isOffline;

  const [icon, label, stateClass] = isOffline
    ? [
        <Trash2 className="w-3 h-3 relative z-10" strokeWidth={2.5} key="icon" />,
        "Saved",
        "bg-[hsl(var(--confidence-high))] text-[hsl(var(--primary-foreground))] border-[hsl(152,60%,30%)]",
      ]
    : isDanger
    ? [
        <AlertTriangle className="w-3 h-3 relative z-10" strokeWidth={2.5} key="icon" />,
        "Full",
        "bg-[hsl(var(--destructive))] text-[hsl(var(--primary-foreground))] border-[hsl(0,70%,40%)]",
      ]
    : [
        <ArrowDown className="w-3 h-3 relative z-10" strokeWidth={2.5} key="icon" />,
        "Download",
        "bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)]",
      ];

  return (
    <button
      type="button"
      onClick={isOffline ? handleRemove : handleSave}
      title={tooltip}
      className={`${comicBase} ${shadowState} ${stateClass}`}
      aria-label={tooltip}
    >
      {icon}
      <span className="relative z-10">{label}</span>
    </button>
  );
}

